import { action, computed, observable, makeObservable, runInAction } from "mobx";
import { MrbBaseInfiniteScrollListViewStore } from "mrb/core";
import { MrbInfiniteScrollGridViewStore } from "mrb/components/infinite-scroll-grid";
import { ProducerPaymentReportFilter } from "administration/producer/filters";
import moment from "moment";
import { applicationDefaults } from "common/constants";
import generateReportRowKey from "./generateReportRowKey";

class BaseProducerRevenueReportViewStore extends MrbBaseInfiniteScrollListViewStore {
    _abortController = null;
    maxDate = null;
    @observable maxFromDate = null;
    @observable minToDate = null;
    maskUserNames = false;
    enforceDateRangeLimit = true;
    @observable.ref containerRef = null;
    @observable.ref summaryData = null;

    @observable reportDownloadInProgress = false;

    @observable selectedReportViewId = 0;

    @observable isDateRangeDisabled = false;

    reportViews = [
        { id: 0, name: "Listeners" },
        { id: 1, name: "Albums/Titles" },
        { id: 2, name: "Tracks" },
    ];

    constructor(rootStore, { maskUserNames = false, enforceDateRangeLimit = true }) {
        super(rootStore, {
            queryConfig: {
                ignoreQueryParams: ["page", "timeZone", "rpp", "orderBy", "producerId"],
                // The query utility evaluates raw query param values, which turns an ISO date into
                // arithmetic: "2025-12-31" becomes 1982 and the picker then shows 01/01/1970. Dates
                // whose month and day both lack a leading zero are the ones that parse - October
                // through December - so opening one of those reports lost its end date. Both bounds
                // are plain ISO strings and must be read back verbatim.
                queryParamMap: {
                    from: { fromQuery: (value) => value },
                    to: { fromQuery: (value) => value },
                },
                filter: new ProducerPaymentReportFilter(),
                pageSize: 30,
                embed: "user,subscriptionPlan",
                orderBy: "producerPayment",
                orderDirection: "desc",
            },
        });
        makeObservable(this);

        this.maxDate = moment().startOf("day").toDate();

        this.maskUserNames = maskUserNames;
        this.enforceDateRangeLimit = enforceDateRangeLimit;

        this.setGridStore(
            new MrbInfiniteScrollGridViewStore(this.rootStore, this.queryUtility, {
                generateKey: (item, index) => generateReportRowKey(this.selectedReportViewId, item, index),
            })
        );

        this.applyFilter = this.applyFilter.bind(this);

        this.fetchProducerReportCsv = this.fetchProducerReportCsv.bind(this);
    }

    async onInit() {
        await Promise.all([this.queryUtility.initialize(), this.fetchSummaryData(this.queryUtility.filter)]);
        this.setMaxFromDate(moment(this.queryUtility.filter.to).toDate());
        this.setMinToDate(moment(this.queryUtility.filter.from).toDate());
    }

    onFetchError(err) {
        if (err.request?.abortSignal?.signal?.aborted) {
            return;
        }
        this.rootStore.notificationStore.error(
            "An unexpected error occurred while trying to load data. Please contact the support team.",
            err
        );
    }

    @action.bound
    setFromDate(value) {
        this.queryUtility.filter.from = moment(value)
            .tz(this.queryUtility.filter.timeZone)
            .startOf("day")
            .format(applicationDefaults.formats.isoDate);
        this.setMinToDate(moment(this.queryUtility.filter.from).toDate());
        this.updateIsDateRangeDisabled();
    }

    @action.bound
    setToDate(value) {
        this.queryUtility.filter.to = moment(value)
            .tz(this.queryUtility.filter.timeZone)
            .endOf("day")
            .format(applicationDefaults.formats.isoDate);

        this.setMaxFromDate(moment(this.queryUtility.filter.to).toDate());
        this.updateIsDateRangeDisabled();
    }

    @action.bound
    setMaxFromDate(value) {
        this.maxFromDate = value;
    }

    @action.bound
    setMinToDate(value) {
        this.minToDate = value;
    }

    @action.bound
    updateIsDateRangeDisabled() {
        if (!this.enforceDateRangeLimit) {
            this.isDateRangeDisabled = false;
            return;
        }
        const from = moment(this.queryUtility.filter.from).startOf("month");
        const to = moment(this.queryUtility.filter.to).endOf("month");
        const diffMonths = to.diff(from, "months", true); // true gives fractional months
        this.isDateRangeDisabled = diffMonths > 6;
    }

    @computed get listenerNumberByUserId() {
        const map = new Map();
        const items = this.gridStore?.data;
        if (items) {
            let counter = 0;
            for (const item of items) {
                if (item && !map.has(item.userId)) {
                    counter += 1;
                    map.set(item.userId, counter);
                }
            }
        }
        return map;
    }

    getListenerNumber(userId) {
        return this.listenerNumberByUserId.get(userId);
    }

    @action.bound
    setSelectedReportViewId(value) {
        this.selectedReportViewId = value;
        this.setGridStore(
            new MrbInfiniteScrollGridViewStore(this.rootStore, this.queryUtility, {
                generateKey: (item, index) => generateReportRowKey(this.selectedReportViewId, item, index),
            })
        );
        this.queryUtility.filter.pageNumber = 1;
        this.queryUtility.initialize();
    }

    async fetchSummaryData(filter) {
        this.rootStore.mainLoaderStore.suspend();
        this.loaderStore.suspend();
        try {
            const data = await this.getSumaryData(filter);
            data.filteredBy = {
                date: filter.monthOfYear,
                timeZone: filter.timeZone,
            };
            runInAction(() => {
                this.setSummaryData(data);
                this.loaderStore.resume();
                this.rootStore.mainLoaderStore.resume();
            });
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
            runInAction(() => {
                this.loaderStore.resume();
                this.rootStore.mainLoaderStore.resume();
            });
        }
    }

    async fetchProducerReportCsv() {
        this.setReportDownloadInProgress(true);
        try {
            const response = await this.getProducerReportTrackItemsCsv(this.queryUtility.filter, this.producerId);

            const blob = new Blob([response], { type: "text/csv" });

            const fileName = `ProducerReport.csv`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        } finally {
            this.setReportDownloadInProgress(false);
        }
    }

    getSumaryData(params) {
        throw new Error("Method not implemented.");
    }

    async getProducerReportTrackItemsCsv(date, producerId) {
        throw new Error("Method not implemented.");
    }

    @action.bound
    setSummaryData(data) {
        data.currentMonthPeriodEndDate = this.queryUtility.filter.to;
        data.currentMonthPeriodStartDate = this.queryUtility.filter.from;
        this.summaryData = data;
    }

    @action.bound
    setReportDownloadInProgress(value) {
        this.reportDownloadInProgress = value;
    }

    @action.bound
    setContainerRef(ref) {
        this.containerRef = ref;
    }

    async applyFilter() {
        await Promise.all([this.queryUtility.fetch(), this.fetchSummaryData(this.queryUtility.filter)]);
    }

    abortExistingRequest() {
        if (this._abortController) {
            this._abortController.abort();
        }
    }
}

export default BaseProducerRevenueReportViewStore;
